import React, { createRef, useContext, useRef, useState } from 'react';
import Form from '@rjsf/material-ui';
import { IChangeEvent } from '@rjsf/core';
import { KatButton, KatDivider } from '@amzn/katal-react';
import styled from 'styled-components';

import { CreateCaseConstants } from 'src/constants/ProjectConstants';
import CreateCaseAttachments from './CreateCaseAttachments';
import UserStaticInfoSection from './UserStaticInfoSection';
import { useAppDispatch, useAppSelector } from 'src/state/store/hooks';
import { addRequestForm } from 'src/state/slice/createCaseSlice';
import debounce from 'lodash/debounce';
import Spacer from 'src/components/Layout/Spacer';
import {
  generateWidgets,
  generateTemplates,
  rjsfValidator,
  transformErrors,
} from 'src/react-jsonschema-katal';
import { logger } from 'src/logger';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { MANDATORY_FIELD_INFORMATION } from 'src/constants/GlobalConstants';
import { DDMAttributeContext } from 'src/integration/ddm/providers/DDMAttributesProvider';
import { RJSFValidationError, UiSchema } from '@rjsf/utils';
import { ErrorMessage } from 'src/components/Error';
import { MetricsName, emitClickMetrics } from 'src/metrics';
import { TranslationContext } from 'src/state/providers/TranslationProvider';

interface ICreateDetailsForm {
  onFormSubmission: () => void;
  submitting?: boolean;
  showReferralInfo?: boolean;
  allowAttachments?: boolean;
}

const FormContainer = styled.div``;

export const MandatoryInformationHeader = () => {
  const { createCaseBundle } = useContext(TranslationContext);
  return (
    <span className="text-size-sm text-secondary">
      {createCaseBundle.getMessage(MANDATORY_FIELD_INFORMATION)}
    </span>
  );
};

const CreateDetailsForm: React.FC<ICreateDetailsForm> = ({
  onFormSubmission,
  submitting = false,
  showReferralInfo = false,
  allowAttachments = false,
}) => {
  const formRef = createRef<any>();
  const [submitted, setSubmitted] = useState<Boolean>(false);
  const ddmContext = useContext(DDMAttributeContext);
  const authContext = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const { fileUploadStatus, attachmentValidationError } = useAppSelector(
    (state) => state.createCase,
  );

  const [createCaseState, setCreateCaseState] = useState(ddmContext.formData);
  const [hasClientSideError, setClientSideError] = useState(false);

  const { createCaseBundle, commonBundle } = useContext(TranslationContext);

  /**
   * Transform Error Messages
   * @param errors
   * @param uiSchema
   * @returns RJSFValidationError
   */
  function transformFormErrors(
    errors: RJSFValidationError[],
    uiSchema?: UiSchema,
  ): RJSFValidationError[] {
    if (errors?.length === 0 && hasClientSideError) {
      setClientSideError(false);
    }
    return transformErrors(errors, uiSchema);
  }

  /**
   * This will invoke when any client side error occurs on the form.
   * Its responsibility is to show the client side error message
   * @param errors
   */
  const handleError = (errors: RJSFValidationError[]) => {
    if (!hasClientSideError) {
      setClientSideError(true);
    }

    if (!submitted) {
      setSubmitted(true); //Setting the submission status as true as only onSubmit can encounter errors
    }
    emitClickMetrics(
      MetricsName.CREATE_CASE_PAGE,
      'ClientSideValidationFailed',
    );
    logger.error(`Create Case validation error: `, {
      errors,
    });
  };

  const handleFormDataChange = ({ formData }: IChangeEvent) => {
    setCreateCaseState(formData);
    dispatch(addRequestForm(formData));
    logger.debug(
      `Added form data for user: ${authContext.customerId} to state. Check data in context`,
      formData,
    );
  };

  /**
   * onSubmit action for Form.
   */
  const handleOnSubmit = () => {
    setClientSideError(false);
    onFormSubmission();
  };

  const showReferralInformationForm =
    !authContext.isAnonymousUser && showReferralInfo;

  //Do liveValidate only after first submit.
  const additionalProps = submitted ? { liveValidate: true } : {};

  return (
    <FormContainer data-testid="RJSFFormContainerTestId">
      <Spacer height="1.25rem" />
      <MandatoryInformationHeader />
      {showReferralInformationForm && (
        <UserStaticInfoSection schema={ddmContext.createCaseStaticSchema} />
      )}

      <Spacer height="1.25rem" />

      <Form
        data-testid="RJSFFormTestId"
        ref={formRef}
        widgets={generateWidgets()}
        formData={createCaseState}
        schema={ddmContext.createCaseSchema}
        validator={rjsfValidator}
        omitExtraData //this property is required to remove the extra data not present in the schema.
        onChange={debounce(handleFormDataChange, 500)}
        onSubmit={handleOnSubmit}
        showErrorList={false} //to disable showing the error list on top.
        uiSchema={ddmContext.createCaseUiSchema}
        templates={generateTemplates()}
        onError={handleError}
        transformErrors={transformFormErrors}
        {...additionalProps}
      >
        {allowAttachments && <CreateCaseAttachments />}
        <KatButton
          type="submit"
          disabled={
            submitting ||
            fileUploadStatus.uploadErrorCount > 0 ||
            fileUploadStatus.currentUploadInProgress !== 0 ||
            !!attachmentValidationError
          }
        >
          {commonBundle.getMessage(CreateCaseConstants.SUBMIT)}
        </KatButton>
        {hasClientSideError && (
          <ErrorMessage
            message={createCaseBundle.getMessage(
              CreateCaseConstants.CREATE_CASE_CLIENT_SIDE_ERRORS,
            )}
          />
        )}
      </Form>
      <KatDivider />
    </FormContainer>
  );
};

export default CreateDetailsForm;
